import { PropsWithChildren, useMemo } from "react";

import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import ArrowDropUpIcon from "@mui/icons-material/ArrowDropUp";
import { Button } from "@mui/material";

import { CabinsColors } from "@/constants/config";
import useThemeData from "@/hooks/useThemeData";
import { FlightItem } from "@/typings";

export const CabinSortMap = {
  Economy: {
    asc: 6,
    desc: 62,
  },
  "Premium Economy": {
    asc: 7,
    desc: 64,
  },
  Business: {
    asc: 8,
    desc: 66,
  },
  First: {
    asc: 9,
    desc: 68,
  },
};

const SortButton = (
  props: PropsWithChildren<{ color: string; value: string; onChange: (v: string) => void }>
) => {
  const { onChange, value, color } = props;
  const { isDarkMode } = useThemeData();
  return (
    <Button
      variant="contained"
      className="flex-1 relative"
      sx={{
        borderRadius: 0,
        backgroundColor: useThemeData().palette.mode === "dark" ? "#272727" : "white",
        borderBottom: `6px solid ${color}`,
        fontWeight: 600,
        // color: "black",
        color: color,
        ".MuiChip-icon": {
          color: "black",
        },
        "&:hover": {
          // backgroundColor: color,
          backgroundColor: isDarkMode ? "#272727" : "white",
          // color: "white",
          color: color,
          // borderBottom: "3px solid rgba(255, 255, 255, 0.7)"
        },
      }}
      onClick={() => {
        if (value === "") {
          onChange("desc");
        }

        // if (value === "") {
        //   onChange("asc");
        // }

        if (value === "desc") {
          onChange("");
        }
      }}
    >
      <span>{props.children}</span>
      <span className="absolute right-[4px] flex flex-col">
        {!props.value && (
          <>
            <ArrowDropUpIcon sx={{ fontSize: 16 }} className="relative bottom-[-4px]" />
            <ArrowDropDownIcon sx={{ fontSize: 16 }} className="relative top-[-4px]" />
          </>
        )}
        {props.value == "desc" && <ArrowDropUpIcon sx={{ fontSize: 16 }} />}
        {props.value == "asc" && <ArrowDropDownIcon sx={{ fontSize: 16 }} />}
      </span>
    </Button>
  );
};

const CabinSort = (props: {
  value: number;
  onChange: (v: number) => void;
  dataSource: FlightItem[];
}) => {
  const { value, onChange } = props;

  const economyValue =
    value === CabinSortMap.Economy.asc ? "desc" : value === CabinSortMap.Economy.desc ? "asc" : "";
  const premiumEconomyValue =
    value === CabinSortMap["Premium Economy"].asc
      ? "desc"
      : value === CabinSortMap["Premium Economy"].desc
      ? "asc"
      : "";
  const businessValue =
    value === CabinSortMap.Business.asc
      ? "desc"
      : value === CabinSortMap.Business.desc
      ? "asc"
      : "";
  const firstValue =
    value === CabinSortMap.First.asc ? "desc" : value === CabinSortMap.First.desc ? "asc" : "";

  const { isDarkMode } = useThemeData();

  const statisticalQuantity = useMemo(() => {
    const res: Record<string, number> = {
      Economy: 0,
      "Premium Economy": 0,
      Business: 0,
      First: 0,
    };
    for (const element of props.dataSource) {
      const { cabin_prices } = element;
      Object.entries(cabin_prices).forEach(([key, value]) => {
        if (value) {
          res[key] = res[key] + 1;
        }
      });
    }

    return res;
  }, [props.dataSource]);

  return (
    <div className="flex sticky top-0 z-[2] mb-2">
      <div className="flex items-center flex-1">
        <div className="w-[40%]" />

        <div className="flex-1 flex space-x-[1px] ml-[1px]">
          <div
            className="w-[124px] text-center"
            style={{
              backgroundColor: isDarkMode ? "#272727" : "white",
            }}
          >
            <Button
              sx={{
                width: "100%",
                borderRadius: 0,
                fontWeight: 600,
              }}
            >
              transfer
            </Button>
          </div>
          <SortButton
            color={CabinsColors.economy}
            value={economyValue}
            onChange={(v) => {
              if (v === "desc") {
                onChange(6);
                return;
              }

              if (v === "asc") {
                onChange(62);
                return;
              }

              onChange(1);
            }}
          >
            {statisticalQuantity["Economy"] > 1000 ? (
              <div className="flex flex-col">
                <span>Economy</span>
                <span className="mt-[-7px]">({statisticalQuantity["Economy"].toLocaleString()})</span>
              </div>
            ) : (
              `Economy(${statisticalQuantity["Economy"]})`
            )}
          </SortButton>

          <SortButton
            value={premiumEconomyValue}
            color={CabinsColors.premiumEconomy}
            onChange={(v) => {
              if (v === "desc") {
                onChange(7);
                return;
              }

              if (v === "asc") {
                onChange(64);
                return;
              }

              onChange(1);
            }}
          >
            {statisticalQuantity["Premium Economy"] > 1000 ? (
              <div className="flex flex-col">
                <span>Prem Econ</span>
                <span className="mt-[-7px]">({statisticalQuantity["Premium Economy"].toLocaleString()})</span>
              </div>
            ) : (
              `Prem Econ(${statisticalQuantity["Premium Economy"]})`
            )}
          </SortButton>

          <SortButton
            color={CabinsColors.business}
            value={businessValue}
            onChange={(v) => {
              if (v === "desc") {
                onChange(8);
                return;
              }

              if (v === "asc") {
                onChange(66);
                return;
              }

              onChange(1);
            }}
          >
            {statisticalQuantity["Business"] > 1000 ? (
              <div className="flex flex-col">
                <span>Business</span>
                <span className="mt-[-7px]">({statisticalQuantity["Business"].toLocaleString()})</span>
              </div>
            ) : (
              `Business(${statisticalQuantity["Business"]})`
            )}
          </SortButton>

          <SortButton
            color={CabinsColors.first}
            value={firstValue}
            onChange={(v) => {
              if (v === "desc") {
                onChange(9);
                return;
              }

              if (v === "asc") {
                onChange(68);
                return;
              }

              onChange(1);
            }}
          >
            {statisticalQuantity["First"] > 1000 ? (
              <div className="flex flex-col">
                <span>First</span>
                <span className="mt-[-7px]">
                  ({statisticalQuantity["First"].toLocaleString()})
                </span>
              </div>
            ) : (
              `First(${statisticalQuantity["First"]})`
            )}
          </SortButton>
        </div>
        {/* <div className="w-[4px]" /> */}
      </div>
      <div className="w-[40px]" />
    </div>
  );
};
export default CabinSort;
